import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthenticatedAdmin } from '@domain/entities';
import { toDomainError, type DomainError } from '@domain/errors';
import type { RightsScope } from '@domain/value-objects';
import { useContainer } from './container-context';

interface AuthState {
  admin: AuthenticatedAdmin | null;
  status: 'loading' | 'anonymous' | 'authenticated';
  error: DomainError | null;
}

interface AuthApi extends AuthState {
  isSuperAdmin: boolean;
  can: (scope: RightsScope, action: 'create' | 'edit' | 'delete') => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const container = useContainer();
  const [state, setState] = useState<AuthState>({
    admin: null,
    status: 'loading',
    error: null,
  });

  /** La politique RBAC applicative doit toujours refléter la session courante. */
  const apply = useCallback(
    (admin: AuthenticatedAdmin | null, status: AuthState['status']) => {
      container.policy.setCurrentAdmin(admin);
      setState({ admin, status, error: null });
    },
    [container],
  );

  useEffect(() => {
    let cancelled = false;
    void container.auth.restoreSession
      .execute()
      .then((admin) => {
        if (cancelled) return;
        apply(admin, admin ? 'authenticated' : 'anonymous');
      })
      .catch(() => {
        if (!cancelled) apply(null, 'anonymous');
      });
    return () => {
      cancelled = true;
    };
  }, [container, apply]);

  useEffect(() => {
    const handler = () => apply(null, 'anonymous');
    window.addEventListener('bbm:unauthenticated', handler);
    return () => window.removeEventListener('bbm:unauthenticated', handler);
  }, [apply]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const admin = await container.auth.signIn.execute({ email, password });
        apply(admin, 'authenticated');
      } catch (error) {
        const domainError = toDomainError(error);
        container.policy.setCurrentAdmin(null);
        setState({ admin: null, status: 'anonymous', error: domainError });
        throw domainError;
      }
    },
    [container, apply],
  );

  const signOut = useCallback(async () => {
    try {
      await container.auth.signOut.execute();
    } finally {
      apply(null, 'anonymous');
    }
  }, [container, apply]);

  const value = useMemo<AuthApi>(
    () => ({
      ...state,
      isSuperAdmin: state.admin?.role === 'SUPER_ADMIN',
      can: (scope: RightsScope, action: 'create' | 'edit' | 'delete'): boolean => {
        if (!state.admin) return false;
        if (state.admin.role === 'SUPER_ADMIN') return true;
        return state.admin.rights?.[scope]?.[action] ?? false;
      },
      signIn,
      signOut,
    }),
    [state, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un <AuthProvider>.');
  return context;
}
